import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Link } from 'react-router-dom';
import { buildingService, type Edificio } from '@/services/buildingService';
import { debtService } from '@/services/debtService';
import { paymentService } from '@/services/paymentService';
import { toast } from 'sonner';

interface BuildingWithFinancials extends Edificio {
  outstandingDebt: number;
  paymentsReceived: number;
  delinquentTenants: number;
}

const DashboardPage: React.FC = () => {
  const [totalOutstandingDebt, setTotalOutstandingDebt] = useState<number>(0);
  const [paymentsReceivedThisMonth, setPaymentsReceivedThisMonth] = useState<number>(0);
  const [delinquentTenantsCount, setDelinquentTenantsCount] = useState<number>(0);
  const [buildings, setBuildings] = useState<BuildingWithFinancials[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [allBuildings, allDebts, allPayments, /* allTenants */] = await Promise.all([
          buildingService.getBuildings(),
          debtService.getAllDebts(),
          paymentService.getAllPayments(), // Assuming this function exists or will be created
          // tenantService.getTenants(),
        ]);

        // Calculate summary data
        const totalDebt = allDebts.reduce((sum, debt) => sum + debt.saldo_pendiente, 0);
        setTotalOutstandingDebt(totalDebt);

        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        const paymentsThisMonth = allPayments.filter(payment => {
          const paymentDate = new Date(payment.fecha_pago);
          return paymentDate.getMonth() === currentMonth && paymentDate.getFullYear() === currentYear;
        }).reduce((sum, payment) => sum + payment.monto_pagado, 0);
        setPaymentsReceivedThisMonth(paymentsThisMonth);

        const today = new Date().toISOString().split('T')[0];
        const delinquentDebtIds = new Set<string>();
        allDebts.forEach(debt => {
          if (!debt.esta_pagada && debt.fecha_vencimiento < today) {
            delinquentDebtIds.add(debt.id_inquilino);
          }
        });
        setDelinquentTenantsCount(delinquentDebtIds.size);

        // Prepare building data for table
        const buildingsWithFinancials: BuildingWithFinancials[] = allBuildings.map(building => {
          const buildingDebts = allDebts.filter(debt => debt.id_edificio === building.id_edificio);
          const buildingPayments = allPayments.filter(payment => {
            const debt = allDebts.find(d => d.id_deuda === payment.id_deuda);
            return debt && debt.id_edificio === building.id_edificio;
          });

          const buildingOutstandingDebt = buildingDebts.reduce((sum, debt) => sum + debt.saldo_pendiente, 0);
          const buildingPaymentsReceived = buildingPayments.reduce((sum, payment) => sum + payment.monto_pagado, 0);

          const buildingDelinquentTenants = new Set<string>();
          buildingDebts.forEach(debt => {
            if (!debt.esta_pagada && debt.fecha_vencimiento < today) {
              buildingDelinquentTenants.add(debt.id_inquilino);
            }
          });

          return {
            ...building,
            outstandingDebt: buildingOutstandingDebt,
            paymentsReceived: buildingPaymentsReceived,
            delinquentTenants: buildingDelinquentTenants.size,
          };
        });
        setBuildings(buildingsWithFinancials);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        setError(err.message);
        toast.error("Error al cargar los datos del dashboard.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredBuildings = buildings.filter(building =>
    building.nombre_edificio.toLowerCase().includes(searchTerm.toLowerCase()) ||
    building.direccion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    building.numero_unidades?.toString().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div>Cargando dashboard...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="px-40 flex flex-1 justify-center py-5">
      <div className="layout-content-container flex flex-col max-w-[960px] flex-1">
        <div className="flex flex-wrap justify-between gap-3 p-4">
          <div className="flex min-w-72 flex-col gap-3">
            <p className="text-[#111418] tracking-light text-[32px] font-bold leading-tight">Dashboard</p>
            <p className="text-[#60758a] text-sm font-normal leading-normal">Resumen de tus propiedades y estado financiero</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-4 p-4">
          <div className="flex min-w-[158px] flex-1 flex-col gap-2 rounded-lg p-6 bg-[#f0f2f5]">
            <p className="text-[#111418] text-base font-medium leading-normal">Deuda Total Pendiente</p>
            <p className="text-[#111418] tracking-light text-2xl font-bold leading-tight">${totalOutstandingDebt.toFixed(2)}</p>
          </div>
          <div className="flex min-w-[158px] flex-1 flex-col gap-2 rounded-lg p-6 bg-[#f0f2f5]">
            <p className="text-[#111418] text-base font-medium leading-normal">Pagos Recibidos Este Mes</p>
            <p className="text-[#111418] tracking-light text-2xl font-bold leading-tight">${paymentsReceivedThisMonth.toFixed(2)}</p>
          </div>
          <div className="flex min-w-[158px] flex-1 flex-col gap-2 rounded-lg p-6 bg-[#f0f2f5]">
            <p className="text-[#111418] text-base font-medium leading-normal">Inquilinos Morosos</p>
            <p className="text-[#111418] tracking-light text-2xl font-bold leading-tight">{delinquentTenantsCount}</p>
          </div>
        </div>
        <h2 className="text-[#111418] text-[22px] font-bold leading-tight tracking-[-0.015em] px-4 pb-3 pt-5">Resumen de Edificios</h2>
        <div className="px-4 py-3">
          <label className="flex flex-col min-w-40 h-12 w-full">
            <div className="flex w-full flex-1 items-stretch rounded-lg h-full">
              <div
                className="text-[#60758a] flex border-none bg-[#f0f2f5] items-center justify-center pl-4 rounded-l-lg border-r-0"
              >
                {/* Icono de lupa */}
                <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" fill="currentColor" viewBox="0 0 256 256">
                  <path
                    d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z"
                  ></path>
                </svg>
              </div>
              <Input
                placeholder="Buscar edificios"
                className="flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-[#111418] focus:outline-0 focus:ring-0 border-none bg-[#f0f2f5] focus:border-none h-full placeholder:text-[#60758a] px-4 rounded-l-none border-l-0 pl-2 text-base font-normal leading-normal"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </label>
        </div>
        <div className="px-4 py-3 @container">
          <div className="flex overflow-hidden rounded-lg border border-[#dbe0e6] bg-white">
            <table className="flex-1">
              <thead>
                <tr className="bg-white">
                  <th className="px-4 py-3 text-left text-[#111418] w-[400px] text-sm font-medium leading-normal">
                    Nombre del Edificio
                  </th>
                  <th className="px-4 py-3 text-left text-[#111418] w-[400px] text-sm font-medium leading-normal">Dirección</th>
                  <th className="px-4 py-3 text-left text-[#111418] w-[400px] text-sm font-medium leading-normal">
                    Total Unidades
                  </th>
                  <th className="px-4 py-3 text-left text-[#111418] w-[400px] text-sm font-medium leading-normal">
                    Deuda Pendiente
                  </th>
                  <th className="px-4 py-3 text-left text-[#111418] w-[400px] text-sm font-medium leading-normal">
                    Pagos Recibidos
                  </th>
                  <th className="px-4 py-3 text-left text-[#111418] w-[400px] text-sm font-medium leading-normal">
                    Inquilinos Morosos
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredBuildings.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-gray-500">No se encontraron edificios.</td>
                  </tr>
                ) : (
                  filteredBuildings.map((building) => (
                    <tr key={building.id_edificio} className="border-t border-t-[#dbe0e6]">
                      <td className="h-[72px] px-4 py-2 w-[400px] text-[#111418] text-sm font-normal leading-normal">
                        <Link to={`/buildings/${building.id_edificio}`} className="text-blue-600 hover:underline">
                          {building.nombre_edificio}
                        </Link>
                      </td>
                      <td className="h-[72px] px-4 py-2 w-[400px] text-[#60758a] text-sm font-normal leading-normal">{building.direccion}</td>
                      <td className="h-[72px] px-4 py-2 w-[400px] text-[#60758a] text-sm font-normal leading-normal">{building.numero_unidades}</td>
                      <td className="h-[72px] px-4 py-2 w-[400px] text-[#60758a] text-sm font-normal leading-normal">${building.outstandingDebt.toFixed(2)}</td>
                      <td className="h-[72px] px-4 py-2 w-[400px] text-[#60758a] text-sm font-normal leading-normal">${building.paymentsReceived.toFixed(2)}</td>
                      <td className="h-[72px] px-4 py-2 w-[400px] text-[#60758a] text-sm font-normal leading-normal">{building.delinquentTenants}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
